import { useRef, useState, useCallback, useEffect } from 'react';

interface MagneticOptions {
  strength?: number;
  easing?: number;
}

export function useMagnetic<T extends HTMLElement = HTMLButtonElement>(
  options: MagneticOptions = {}
) {
  const ref = useRef<T>(null);
  const { strength = 0.15, easing = 0.2 } = options;
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  const handleMouse = useCallback(
    (e: React.MouseEvent) => {
      if (isTouchDevice) return;
      const el = ref.current;
      if (!el) return;

      const { clientX, clientY } = e;
      const { left, top, width, height } = el.getBoundingClientRect();
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      const deltaX = (clientX - centerX) * strength;
      const deltaY = (clientY - centerY) * strength;

      setPosition({ x: deltaX, y: deltaY });
    },
    [strength, isTouchDevice]
  );

  const handleTouch = useCallback(
    (e: React.TouchEvent) => {
      if (!isTouchDevice) return;
      const el = ref.current;
      if (!el) return;

      const touch = e.touches[0];
      const { clientX, clientY } = touch;
      const { left, top, width, height } = el.getBoundingClientRect();
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      const deltaX = (clientX - centerX) * strength * 0.5;
      const deltaY = (clientY - centerY) * strength * 0.5;

      setPosition({ x: deltaX, y: deltaY });
    },
    [strength, isTouchDevice]
  );

  const reset = useCallback(() => {
    setPosition({ x: 0, y: 0 });
  }, []);

  const style = {
    transform: `translate(${position.x}px, ${position.y}px)`,
    transition: `transform ${easing}s cubic-bezier(0.23, 1, 0.32, 1)`,
  };

  return {
    ref,
    style,
    onMouseMove: handleMouse,
    onMouseLeave: reset,
    onTouchStart: handleTouch,
    onTouchEnd: reset,
  };
}
